import React from 'react';
import styles from './Transition.module.css';
import { FilterIcon, SearchIcon } from './icons';
import { COLUMN_DEFS, MIGRATION_TYPES, PAGE_SIZE, STATUS_OPTIONS, statusLabel } from './constants';
import { showsFileStatus } from './records';

const SORTABLE = new Set(['item', 'function', 'status', 'date', 'owner']);

const HEADERS = COLUMN_DEFS;

const DATE_FILTER_LABELS = {
    overdue: 'Overdue',
    due_7: 'Due in 7 days',
    due_30: 'Due in 30 days',
};

export default function Tracker({
    rows,
    totalCount,
    hasRecords = false,
    loading = false,
    search,
    view,
    statusFilter,
    functionFilter,
    ownerFilter,
    migrationFilter,
    dateFilter,
    filterOptions,
    filterCount,
    canWrite = true,
    columns,
    selected,
    inlineEdit,
    page,
    sort,
    menu,
    onSearch,
    onView,
    onStatusFilter,
    onFunctionFilter,
    onOwnerFilter,
    onMigrationFilter,
    onDateFilter,
    onClearFilters,
    onToggleMenu,
    onCloseMenu,
    onToggleRow,
    onTogglePage,
    onSort,
    onPage,
    onOpen,
    onChangeItem,
    onAdd,
    onImport,
    onPaste,
    onColumns,
    onExport,
    onToggleEdit,
    onBulkApply,
    views = [{ key: 'all', label: 'All items' }],
    viewLabel = 'All items',
    pageSize = PAGE_SIZE,
}) {
    const pageCount = Math.max(1, Math.ceil((totalCount || 0) / pageSize));
    const safePage = Math.min(page, pageCount);
    const visible = rows;
    const visibleIds = visible.map(item => item.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.includes(id));
    const shownColumns = HEADERS.filter(column => columns[column.id]);

    return (
        <div>
            <div className={styles.toolbar}>
                <label className={styles.search}>
                    <span className={styles.searchIcon}>
                        <SearchIcon />
                    </span>
                    <input className={styles.input} value={search} placeholder="Search items..." aria-label="Search items" onChange={event => onSearch(event.target.value)} />
                </label>
                <span className={styles.viewLabel}>View</span>
                <div className={styles.popoverWrap}>
                    <button type="button" className={styles.toolbarBtn} onClick={() => onToggleMenu('view')}>
                        {viewLabel} ▾
                    </button>
                    {menu === 'view' ? (
                        <div className={styles.menu} role="menu">
                            {views.map(option => (
                                <button key={option.key} type="button" onClick={() => onView(option.key, option.label)}>
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className={styles.popoverWrap}>
                    <button type="button" className={filterCount ? `${styles.toolbarBtn} ${styles.toolbarBtnActive}` : styles.toolbarBtn} onClick={() => onToggleMenu('filters')}>
                        <FilterIcon /> Filters{filterCount ? ` ${filterCount}` : ''}
                    </button>
                    {menu === 'filters' ? (
                        <div className={styles.filterPanel} role="dialog" aria-label="Filters">
                            <p className={styles.filterPanelTitle}>Status</p>
                            <button type="button" className={styles.filterOption} onClick={() => onStatusFilter(null)}>
                                Any status
                            </button>
                            {STATUS_OPTIONS.map(status => (
                                <button key={status.value} type="button" className={statusFilter === status.value ? styles.filterOptionActive : styles.filterOption} onClick={() => onStatusFilter(status.value)}>
                                    {status.label}
                                </button>
                            ))}
                            <button type="button" className={statusFilter === 'attention' ? styles.filterOptionActive : styles.filterOption} onClick={() => onStatusFilter('attention')}>
                                Needs attention
                            </button>
                            <p className={styles.filterPanelTitle}>Business function</p>
                            {(filterOptions?.business_function || []).slice(0, 12).map(label => (
                                <button key={label} type="button" className={functionFilter === label ? styles.filterOptionActive : styles.filterOption} onClick={() => onFunctionFilter(functionFilter === label ? null : label)}>
                                    {label}
                                </button>
                            ))}
                            <p className={styles.filterPanelTitle}>Owner</p>
                            {(filterOptions?.owner || []).slice(0, 8).map(label => (
                                <button key={label} type="button" className={ownerFilter === label ? styles.filterOptionActive : styles.filterOption} onClick={() => onOwnerFilter(ownerFilter === label ? null : label)}>
                                    {label}
                                </button>
                            ))}
                            <p className={styles.filterPanelTitle}>Migration type</p>
                            {(filterOptions?.migration_type || []).slice(0, 8).map(label => (
                                <button key={label} type="button" className={migrationFilter === label ? styles.filterOptionActive : styles.filterOption} onClick={() => onMigrationFilter(migrationFilter === label ? null : label)}>
                                    {label}
                                </button>
                            ))}
                            <p className={styles.filterPanelTitle}>Target date</p>
                            {(filterOptions?.target_date || ['overdue', 'due_7', 'due_30'])
                                .filter(key => key !== 'custom')
                                .map(key => (
                                <button key={key} type="button" className={dateFilter === key ? styles.filterOptionActive : styles.filterOption} onClick={() => onDateFilter(dateFilter === key ? null : key)}>
                                    {DATE_FILTER_LABELS[key] || key}
                                </button>
                            ))}
                            <button type="button" className={styles.filterClearBtn} onClick={onClearFilters}>
                                Clear all filters
                            </button>
                        </div>
                    ) : null}
                </div>
                <button type="button" className={styles.toolbarBtn} onClick={onColumns}>
                    Columns
                </button>
                <div className={styles.toolbarRight}>
                    <button type="button" className={styles.primaryBtn} onClick={onAdd} disabled={!canWrite} title={canWrite ? undefined : 'You do not have permission to add rows.'}>
                        Add row
                    </button>
                    <button type="button" className={styles.toolbarBtn} onClick={onPaste} disabled={!canWrite} title={canWrite ? undefined : 'You do not have permission to paste rows.'}>
                        Paste rows
                    </button>
                    <button type="button" className={styles.toolbarBtn} onClick={() => onExport()}>
                        Export
                    </button>
                    <div className={styles.popoverWrap}>
                        <button type="button" className={styles.toolbarBtn} onClick={() => onToggleMenu('more')}>
                            More
                        </button>
                        {menu === 'more' ? (
                            <div className={styles.menu}>
                                <button type="button" onClick={onToggleEdit}>
                                    {inlineEdit ? 'Stop editing' : 'Edit in table'}
                                </button>
                                <button type="button" onClick={() => onExport()}>
                                    Export view
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            {selected.length > 0 ? (
                <BulkBar count={selected.length} onApply={onBulkApply} onExport={() => onExport(selected)} />
            ) : null}
            {filterCount ? (
                <div className={styles.chips}>
                    {statusFilter ? (
                        <span className={styles.filterChip}>
                            Status: {statusFilter === 'attention' ? 'Needs attention' : statusLabel(statusFilter)}
                            <button type="button" onClick={() => onStatusFilter(null)} aria-label="Remove status filter">
                                ×
                            </button>
                        </span>
                    ) : null}
                    {functionFilter ? (
                        <span className={styles.filterChip}>
                            Function: {functionFilter}
                            <button type="button" onClick={() => onFunctionFilter(null)} aria-label="Remove function filter">
                                ×
                            </button>
                        </span>
                    ) : null}
                    {ownerFilter ? (
                        <span className={styles.filterChip}>
                            Owner: {ownerFilter}
                            <button type="button" onClick={() => onOwnerFilter(null)} aria-label="Remove owner filter">
                                ×
                            </button>
                        </span>
                    ) : null}
                    {migrationFilter ? (
                        <span className={styles.filterChip}>
                            Migration: {migrationFilter}
                            <button type="button" onClick={() => onMigrationFilter(null)} aria-label="Remove migration filter">
                                ×
                            </button>
                        </span>
                    ) : null}
                    {dateFilter ? (
                        <span className={styles.filterChip}>
                            Date: {DATE_FILTER_LABELS[dateFilter] || dateFilter}
                            <button type="button" onClick={() => onDateFilter(null)} aria-label="Remove date filter">
                                ×
                            </button>
                        </span>
                    ) : null}
                    <button type="button" className={styles.textBtn} onClick={onClearFilters}>
                        Clear all
                    </button>
                </div>
            ) : null}
            <p className={styles.metaLine}>
                {totalCount} authorised {totalCount === 1 ? 'item' : 'items'} · {viewLabel}
                {search ? ` · Search: “${search}”` : ''}
            </p>
            <div className={styles.tableWrap} style={{ marginTop: 8 }} onMouseDown={onCloseMenu}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.checkCell}>
                                <input
                                    className={styles.check}
                                    type="checkbox"
                                    aria-label="Select all visible rows"
                                    checked={allVisibleSelected}
                                    onChange={() => onTogglePage(visibleIds)}
                                />
                            </th>
                            {shownColumns.map(column => (
                                <th key={column.id}>
                                    {SORTABLE.has(column.id) ? (
                                        <button type="button" className={styles.sortBtn} onClick={() => onSort(column.id)}>
                                            {column.label} {sort.key === column.id ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                                        </button>
                                    ) : (
                                        column.label
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.length === 0 ? (
                            <tr>
                                <td colSpan={shownColumns.length + 1}>
                                    <EmptyState
                                        loading={loading}
                                        hasItems={hasRecords || totalCount > 0 || search || filterCount > 0 || view !== 'all'}
                                        onImport={onImport}
                                        onClear={() => {
                                            onSearch('');
                                            onClearFilters();
                                            onView('all', 'All items');
                                        }}
                                    />
                                </td>
                            </tr>
                        ) : (
                            visible.map(item => (
                                <tr key={item.id}>
                                    <td>
                                        <input
                                            className={styles.check}
                                            type="checkbox"
                                            aria-label={`Select ${item.title}`}
                                            checked={selected.includes(item.id)}
                                            onChange={() => onToggleRow(item.id)}
                                        />
                                    </td>
                                    {shownColumns.map(column => (
                                        <td key={column.id}>
                                            <Cell item={item} column={column.id} onOpen={onOpen} onChangeItem={onChangeItem} canEdit={inlineEdit && canWrite} />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                {visible.length > 0 ? (
                    <div className={styles.pager}>
                        <span>
                            Page {safePage} of {pageCount}
                        </span>
                        <button type="button" className={styles.toolbarBtn} disabled={safePage === 1} onClick={() => onPage(safePage - 1)}>
                            Previous
                        </button>
                        <button type="button" className={styles.toolbarBtn} disabled={safePage === pageCount} onClick={() => onPage(safePage + 1)}>
                            Next
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function EmptyState({ loading, hasItems, onImport, onClear }) {
    if (loading && hasItems) {
        return (
            <div className={styles.emptyTable}>
                <p className={styles.finePrint}>Loading items…</p>
            </div>
        );
    }
    if (!hasItems) {
        return (
            <div className={styles.emptyTable}>
                <h3>No tracker imported yet.</h3>
                <p>Import an existing spreadsheet to start managing your transition.</p>
                <div style={{ marginTop: 16 }}>
                    <button type="button" className={styles.primaryBtn} onClick={onImport}>
                        Import spreadsheet
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className={styles.emptyTable}>
            <h3>No items match your filters.</h3>
            <p>Try changing the filters or searching for something else.</p>
            <div style={{ marginTop: 16 }}>
                <button type="button" className={styles.linkBtn} onClick={onClear}>
                    Clear filters
                </button>
            </div>
        </div>
    );
}

function Cell({ item, column, onOpen, onChangeItem, canEdit = true }) {
    if (column === 'item') {
        return (
            <div>
                <button type="button" className={styles.itemBtn} onClick={() => onOpen(item.id)}>
                    {item.title}
                </button>
                <p className={styles.itemSub}>{[item.code, item.summary].filter(Boolean).join(' · ')}</p>
            </div>
        );
    }
    if (column === 'function') return item.businessFunction;
    if (column === 'owner') return item.owner;
    if (column === 'attention') return <span className={item.attention ? styles.warn : styles.quiet}>{item.attention || '—'}</span>;
    if (column === 'source') return item.source;
    if (column === 'description') return item.description;
    if (column === 'custom') return item.deliveryNote || '—';
    if (column === 'migration') {
        if (!canEdit) return item.migrationType;
        return <InlineSelect value={item.migrationType} options={MIGRATION_TYPES} ariaLabel={`Migration type for ${item.title}`} onChange={value => onChangeItem(item.id, { migrationType: value })} />;
    }
    if (column === 'status') {
        return (
            <div>
                {canEdit ? (
                    <InlineSelect
                        value={item.status}
                        options={STATUS_OPTIONS.map(option => option.label)}
                        ariaLabel={`Status for ${item.title}`}
                        onChange={value => onChangeItem(item.id, { status: value })}
                    />
                ) : (
                    item.status
                )}
                {showsFileStatus(item) ? <p className={styles.itemSub}>From file: {item.statusRaw}</p> : null}
            </div>
        );
    }
    if (column === 'date') {
        if (!canEdit) return item.targetDate || '—';
        return (
            <input
                className={styles.cellDate}
                type="date"
                aria-label={`Target date for ${item.title}`}
                value={item.targetDate || ''}
                onChange={event => onChangeItem(item.id, { targetDate: event.target.value })}
            />
        );
    }
    return null;
}

function InlineSelect({ value, options, ariaLabel, onChange }) {
    return (
        <select className={styles.cellSelect} aria-label={ariaLabel} value={value} onChange={event => onChange(event.target.value)}>
            {options.map(option => (
                <option key={option} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
}

function BulkBar({ count, onApply, onExport }) {
    const [open, setOpen] = React.useState(null);
    return (
        <div className={styles.bulkBar}>
            <span className={styles.bulkCount}>{count} selected</span>
            <BulkControl label="Edit status" open={open === 'status'} onToggle={() => setOpen(open === 'status' ? null : 'status')} options={STATUS_OPTIONS.map(option => option.label)} onPick={value => { onApply({ status: value }); setOpen(null); }} />
            <BulkOwner open={open === 'owner'} onToggle={() => setOpen(open === 'owner' ? null : 'owner')} onPick={value => { onApply({ owner: value }); setOpen(null); }} />
            <BulkDate open={open === 'date'} onToggle={() => setOpen(open === 'date' ? null : 'date')} onPick={value => { onApply({ targetDate: value }); setOpen(null); }} />
            <BulkControl label="Set migration type" open={open === 'migration'} onToggle={() => setOpen(open === 'migration' ? null : 'migration')} options={MIGRATION_TYPES} onPick={value => { onApply({ migrationType: value }); setOpen(null); }} />
            <button type="button" className={styles.toolbarBtn} onClick={onExport}>
                Export selected
            </button>
        </div>
    );
}

function BulkOwner({ open, onToggle, onPick }) {
    const [value, setValue] = React.useState('');
    return (
        <div className={styles.popoverWrap}>
            <button type="button" className={styles.toolbarBtn} onClick={onToggle}>
                Assign owner
            </button>
            {open ? (
                <div className={styles.menu}>
                    <input className={styles.input} aria-label="Owner name" value={value} onChange={event => setValue(event.target.value)} />
                    <button type="button" onClick={() => value.trim() && onPick(value.trim())}>
                        Apply owner
                    </button>
                </div>
            ) : null}
        </div>
    );
}

function BulkControl({ label, open, onToggle, options, onPick }) {
    return (
        <div className={styles.popoverWrap}>
            <button type="button" className={styles.toolbarBtn} onClick={onToggle}>
                {label}
            </button>
            {open ? (
                <div className={styles.menu}>
                    {options.map(option => (
                        <button key={option} type="button" onClick={() => onPick(option)}>
                            {option}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function BulkDate({ open, onToggle, onPick }) {
    return (
        <div className={styles.popoverWrap}>
            <button type="button" className={styles.toolbarBtn} onClick={onToggle}>
                Set date
            </button>
            {open ? (
                <div className={styles.menu} style={{ padding: 10 }}>
                    <input className={styles.input} type="date" aria-label="Set target date" onChange={event => event.target.value && onPick(event.target.value)} />
                </div>
            ) : null}
        </div>
    );
}
